import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { db } from '../db';
import { ArrowRight, Users, UserPlus } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  'Food',
  'Transport',
  'Accommodation',
  'Activities',
  'Shopping',
  'Other',
];

const AddExpenseModal = ({ open, onClose, onAdd, editMode = false, initialData = null }) => {
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [category, setCategory] = useState('Food');
  const [note, setNote] = useState('');
  const [baseCurrency, setBaseCurrency] = useState('USD');
  const [convertedAmount, setConvertedAmount] = useState(null);
  const [isConverting, setIsConverting] = useState(false);
  const [showIOU, setShowIOU] = useState(false);
  const [iouFriendName, setIouFriendName] = useState('');
  const [iouAmount, setIouAmount] = useState('');
  const [knownFriends, setKnownFriends] = useState([]);
  const [showFriendSuggestions, setShowFriendSuggestions] = useState(false);

  useEffect(() => {
    // Load base currency and last expense currency
    const loadCurrencies = async () => {
      const currencySetting = await db.settings.get('currency');
      const lastExpenseCurrency = await db.settings.get('last_expense_currency');
      
      if (currencySetting) {
        setBaseCurrency(currencySetting.value);
        
        if (!editMode) {
          if (lastExpenseCurrency) {
            setCurrency(lastExpenseCurrency.value);
          } else {
            setCurrency(currencySetting.value);
          }
        }
      }
    };
    loadCurrencies();

    // Load known friend names from IOUs
    const loadFriends = async () => {
      try {
        const allIOUs = await db.ious.toArray();
        const names = [...new Set(allIOUs.map(i => i.friendName))];
        setKnownFriends(names);
      } catch (e) {}
    };
    if (open) loadFriends();
  }, [editMode, open]);

  useEffect(() => {
    // Load initial data when editing or prefilling from converter
    if (initialData) {
      setAmount(initialData.amount.toString());
      setCurrency(initialData.currency);
      if (initialData.category) {
        setCategory(initialData.category);
      }
      setNote(initialData.note || '');
    } else if (!editMode && open) {
      // Reset form when opening fresh (not editing, no prefill)
      setAmount('');
      setNote('');
      setCategory('Food');
      setShowIOU(false);
      setIouFriendName('');
      setIouAmount('');
      setShowFriendSuggestions(false);
    }
  }, [editMode, initialData, open]);

  useEffect(() => {
    // Convert currency in real-time using CACHED rates (no API calls)
    const convertCurrency = async () => {
      if (!amount || parseFloat(amount) <= 0 || currency === baseCurrency) {
        setConvertedAmount(null);
        return;
      }

      setIsConverting(true);
      try {
        // First, try to use cached bulk rates from IndexedDB (NO API CALL)
        const stored = await db.settings.get('bulk_exchange_rates');
        if (stored?.value) {
          const parsed = JSON.parse(stored.value);
          const rates = parsed.rates;
          
          const expenseRate = rates[currency] || 1;
          const baseRate = rates[baseCurrency] || 1;
          
          const converted = (parseFloat(amount) / expenseRate) * baseRate;
          setConvertedAmount(converted);
          setIsConverting(false);
          return;
        }
        
        // Fallback: fetch bulk rates (ONE API call, then cached)
        const response = await axios.get(`${API}/exchange-rates`);
        const rates = response.data.rates;
        
        // Cache for future use
        await db.settings.put({
          key: 'bulk_exchange_rates',
          value: JSON.stringify({
            rates: rates,
            last_updated: response.data.last_updated
          })
        });
        
        const expenseRate = rates[currency] || 1;
        const baseRate = rates[baseCurrency] || 1;
        
        const converted = (parseFloat(amount) / expenseRate) * baseRate;
        setConvertedAmount(converted);
      } catch (error) {
        console.error('Conversion failed:', error);
        // Try individual cached rates as last resort
        try {
          const expenseCurrencyRate = await db.exchangeRates.get(currency);
          const baseCurrencyRate = await db.exchangeRates.get(baseCurrency);
          
          if (expenseCurrencyRate && baseCurrencyRate) {
            const converted = (parseFloat(amount) / expenseCurrencyRate.rate) * baseCurrencyRate.rate;
            setConvertedAmount(converted);
          }
        } catch (err) {
          console.error('Offline conversion failed:', err);
        }
      } finally {
        setIsConverting(false);
      }
    };

    const timeoutId = setTimeout(convertCurrency, 500);
    return () => clearTimeout(timeoutId);
  }, [amount, currency, baseCurrency]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!amount || parseFloat(amount) <= 0) {
      return;
    }

    const expense = {
      amount: parseFloat(amount),
      currency,
      category,
      note,
      date: new Date().toISOString().split('T')[0],
    };

    // Save IOU if toggled
    if (showIOU && iouFriendName.trim() && iouAmount && parseFloat(iouAmount) > 0) {
      try {
        await db.ious.add({
          friendName: iouFriendName.trim(),
          amount: parseFloat(iouAmount),
          currency,
          note: note || `${category} expense`,
          date: new Date().toISOString().split('T')[0],
          paidBack: 0
        });
      } catch (err) {
        console.error('Failed to save IOU:', err);
      }
    }

    onAdd(expense);
    
    // Reset form
    setAmount('');
    setNote('');
    setShowIOU(false);
    setIouFriendName('');
    setIouAmount('');
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="add-expense-modal" className="bg-card border-border max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-2xl font-heading">
            {editMode ? 'Edit Expense' : 'Add Expense'}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="amount">Amount</Label>
            <Input
              id="amount"
              data-testid="expense-amount-input"
              type="number"
              step="0.01"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="bg-secondary/50 border-transparent text-2xl h-14"
              autoFocus
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="currency">Currency</Label>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger data-testid="expense-currency-select" id="currency" className="">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                <SelectItem value="USD">USD - United States</SelectItem>
                <SelectItem value="EUR">EUR - European Union</SelectItem>
                <SelectItem value="GBP">GBP - United Kingdom</SelectItem>
                <SelectItem value="JPY">JPY - Japan</SelectItem>
                <SelectItem value="AUD">AUD - Australia</SelectItem>
                <SelectItem value="CAD">CAD - Canada</SelectItem>
                <SelectItem value="CHF">CHF - Switzerland</SelectItem>
                <SelectItem value="CNY">CNY - China</SelectItem>
                <SelectItem value="SEK">SEK - Sweden</SelectItem>
                <SelectItem value="NZD">NZD - New Zealand</SelectItem>
                <SelectItem value="MXN">MXN - Mexico</SelectItem>
                <SelectItem value="SGD">SGD - Singapore</SelectItem>
                <SelectItem value="HKD">HKD - Hong Kong</SelectItem>
                <SelectItem value="NOK">NOK - Norway</SelectItem>
                <SelectItem value="KRW">KRW - South Korea</SelectItem>
                <SelectItem value="TRY">TRY - Turkey</SelectItem>
                <SelectItem value="INR">INR - India</SelectItem>
                <SelectItem value="RUB">RUB - Russia</SelectItem>
                <SelectItem value="BRL">BRL - Brazil</SelectItem>
                <SelectItem value="ZAR">ZAR - South Africa</SelectItem>
                <SelectItem value="THB">THB - Thailand</SelectItem>
                <SelectItem value="IDR">IDR - Indonesia</SelectItem>
                <SelectItem value="MYR">MYR - Malaysia</SelectItem>
                <SelectItem value="PHP">PHP - Philippines</SelectItem>
                <SelectItem value="VND">VND - Vietnam</SelectItem>
                <SelectItem value="CZK">CZK - Czech Republic</SelectItem>
                <SelectItem value="PLN">PLN - Poland</SelectItem>
                <SelectItem value="AED">AED - UAE</SelectItem>
                <SelectItem value="ILS">ILS - Israel</SelectItem>
                <SelectItem value="ARS">ARS - Argentina</SelectItem>
                <SelectItem value="CLP">CLP - Chile</SelectItem>
                <SelectItem value="COP">COP - Colombia</SelectItem>
                <SelectItem value="PEN">PEN - Peru</SelectItem>
                <SelectItem value="EGP">EGP - Egypt</SelectItem>
                <SelectItem value="DKK">DKK - Denmark</SelectItem>
                <SelectItem value="HUF">HUF - Hungary</SelectItem>
                <SelectItem value="ISK">ISK - Iceland</SelectItem>
                <SelectItem value="MAD">MAD - Morocco</SelectItem>
                <SelectItem value="TWD">TWD - Taiwan</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="category">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="expense-category-select" id="category" className="">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="note">Note (Optional)</Label>
            <Input
              id="note"
              data-testid="expense-note-input"
              placeholder="Add details..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="bg-secondary/50 border-transparent"
            />
          </div>

          {/* IOU Split Toggle */}
          <div className="space-y-3">
            <Button
              type="button"
              data-testid="iou-toggle-button"
              variant={showIOU ? "default" : "outline"}
              onClick={() => setShowIOU(!showIOU)}
              className={`w-full rounded-full ${showIOU ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'border-amber-500/40 text-amber-500 hover:bg-amber-500/10'}`}
            >
              <Users className="w-4 h-4 mr-2" />
              {showIOU ? 'Splitting this bill' : 'Split with a friend?'}
            </Button>

            {showIOU && (
              <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4 space-y-3">
                <div className="space-y-2">
                  <Label>Friend's Name</Label>
                  <div className="relative">
                    <Input
                      data-testid="iou-friend-name-input"
                      placeholder="Who owes you?"
                      value={iouFriendName}
                      onChange={(e) => {
                        setIouFriendName(e.target.value);
                        setShowFriendSuggestions(e.target.value.length === 0 && knownFriends.length > 0);
                      }}
                      onFocus={() => setShowFriendSuggestions(knownFriends.length > 0)}
                      onBlur={() => setTimeout(() => setShowFriendSuggestions(false), 200)}
                      className="bg-secondary/50 border-transparent"
                    />
                    {showFriendSuggestions && knownFriends.length > 0 && (
                      <div className="absolute z-20 top-full mt-1 left-0 right-0 bg-card border border-border rounded-lg shadow-lg max-h-40 overflow-y-auto" data-testid="friend-suggestions">
                        {knownFriends
                          .filter(n => !iouFriendName || n.toLowerCase().includes(iouFriendName.toLowerCase()))
                          .map((name) => (
                            <button
                              key={name}
                              type="button"
                              className="w-full text-left px-4 py-2.5 text-sm hover:bg-secondary/50 transition-colors flex items-center gap-2"
                              onMouseDown={() => {
                                setIouFriendName(name);
                                setShowFriendSuggestions(false);
                              }}
                            >
                              <Users className="w-3.5 h-3.5 text-muted-foreground" />
                              {name}
                            </button>
                          ))}
                        <button
                          type="button"
                          className="w-full text-left px-4 py-2.5 text-sm hover:bg-secondary/50 transition-colors flex items-center gap-2 text-amber-500 border-t border-border"
                          onMouseDown={() => {
                            setIouFriendName('');
                            setShowFriendSuggestions(false);
                          }}
                        >
                          <UserPlus className="w-3.5 h-3.5" />
                          Add new friend
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Their share ({currency})</Label>
                  <Input
                    data-testid="iou-amount-input"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={iouAmount}
                    onChange={(e) => setIouAmount(e.target.value)}
                    className="bg-secondary/50 border-transparent"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Currency Conversion Display */}
          {convertedAmount !== null && currency !== baseCurrency && (
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-3 flex items-center justify-center gap-2">
              <span className="text-foreground font-semibold">{amount} {currency}</span>
              <ArrowRight className="w-4 h-4 text-primary" />
              <span className="text-primary font-bold">{convertedAmount.toFixed(2)} {baseCurrency}</span>
            </div>
          )}

          {isConverting && currency !== baseCurrency && (
            <p className="text-xs text-center text-muted-foreground">Converting...</p>
          )}

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 rounded-full border-border"
              data-testid="cancel-expense-button"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 rounded-full bg-primary text-primary-foreground"
              data-testid="save-expense-button"
            >
              {editMode ? 'Update Expense' : 'Add Expense'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddExpenseModal;